import { useState, useCallback, useRef, useEffect } from 'react';
import { NS } from '@ownyou/shared-types';
import { useTrigger } from '../contexts/TriggerContext';
import { useEngagementLogger } from './useEngagementLogger';
import { useStore } from '../contexts/StoreContext';
import { useAuth } from '../contexts/AuthContext';
import type { MissionCard } from '@ownyou/shared-types';
import type { RefinementResultPayload } from '../workers/worker-protocol';

interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
}

export function useMissionRefiner(missionId: string, missionType?: string) {
  const [history, setHistory] = useState<ConversationMessage[]>([]);
  const [isRefining, setIsRefining] = useState(false);
  const isRefiningRef = useRef(false);
  const historyRef = useRef<ConversationMessage[]>([]);
  historyRef.current = history;
  const [updatedCard, setUpdatedCard] = useState<MissionCard | null>(null);
  const { getWorker } = useTrigger();
  const logger = useEngagementLogger();
  const { store } = useStore();
  const { wallet } = useAuth();
  const userId = wallet?.address ?? 'anonymous';

  const activeHandlerRef = useRef<((event: MessageEvent) => void) | null>(null);
  const activeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load persisted history on mount
  useEffect(() => {
    if (!store || !missionId) return;
    async function loadHistory() {
      try {
        const stored = await store!.get(NS.refinementHistory(userId), missionId);
        const data = (stored as { value?: { messages?: ConversationMessage[] } } | null)?.value;
        if (data?.messages?.length) {
          setHistory(data.messages);
        }
      } catch { /* best effort */ }
    }
    loadHistory();
  }, [store, userId, missionId]);

  useEffect(() => {
    return () => {
      const worker = getWorker();
      if (activeHandlerRef.current && worker) {
        worker.removeEventListener('message', activeHandlerRef.current);
        activeHandlerRef.current = null;
      }
      if (activeTimeoutRef.current) {
        clearTimeout(activeTimeoutRef.current);
        activeTimeoutRef.current = null;
      }
    };
  }, [getWorker]);

  // Persist history to Store
  const persistHistory = useCallback(async (messages: ConversationMessage[]) => {
    if (!store) return;
    try {
      await store.put(NS.refinementHistory(userId), missionId, {
        messages, lastUpdated: Date.now(),
      });
    } catch { /* best effort */ }
  }, [store, userId, missionId]);

  const refine = useCallback(
    async (message: string) => {
      const worker = getWorker();
      if (!worker || isRefiningRef.current) return;

      setIsRefining(true);
      isRefiningRef.current = true;
      const updatedHistory = [...historyRef.current, { role: 'user' as const, content: message }];
      setHistory(updatedHistory);
      persistHistory(updatedHistory);

      worker.postMessage({
        type: 'REFINE_MISSION',
        payload: { missionId, message, history: updatedHistory },
      });

      if (activeHandlerRef.current) {
        worker.removeEventListener('message', activeHandlerRef.current);
      }
      if (activeTimeoutRef.current) {
        clearTimeout(activeTimeoutRef.current);
      }

      const handler = (event: MessageEvent) => {
        if (
          event.data.type === 'REFINEMENT_RESULT' &&
          event.data.payload.missionId === missionId
        ) {
          const result: RefinementResultPayload = event.data.payload;
          const withResponse = [...updatedHistory, { role: 'assistant' as const, content: result.assistantMessage }];
          setHistory(withResponse);
          persistHistory(withResponse);
          if (result.updatedCard) setUpdatedCard(result.updatedCard);
          setIsRefining(false);
          isRefiningRef.current = false;
          worker.removeEventListener('message', handler);
          activeHandlerRef.current = null;
          if (activeTimeoutRef.current) {
            clearTimeout(activeTimeoutRef.current);
            activeTimeoutRef.current = null;
          }

          logger?.log({
            missionId,
            action: 'refined',
            agentType: missionType ?? 'unknown',
            refinementMessage: message,
            profileSignalExtracted: result.profileSignal?.preference,
            isComposite: false,
            isCascade: false,
            source: 'detection',
          });
        }
      };
      worker.addEventListener('message', handler);
      activeHandlerRef.current = handler;

      activeTimeoutRef.current = setTimeout(() => {
        worker.removeEventListener('message', handler);
        activeHandlerRef.current = null;
        activeTimeoutRef.current = null;
        if (isRefiningRef.current) {
          const timeoutHistory = [
            ...historyRef.current,
            { role: 'assistant' as const, content: 'Refinement timed out. Please try again.' },
          ];
          setHistory(timeoutHistory);
          persistHistory(timeoutHistory);
          setIsRefining(false);
          isRefiningRef.current = false;
        }
      }, 15000);
    },
    [getWorker, missionId, missionType, logger, persistHistory],
  );

  return { refine, history, isRefining, updatedCard };
}
